import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowRight, Database, Users, Calendar, Settings } from "lucide-react";
import { School } from "@/hooks/data/useSchools";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toArabicNumbers } from "@/utils/arabicNumbers";
import { useEffect, useState } from "react";
import { useSchools } from "../../../hooks/data/useSchools";
import { Tables } from "@/integrations/supabase/types";

interface SchoolDataViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  school: School;
}

const SchoolDataView = ({
  open,
  onOpenChange,
  school,
}: SchoolDataViewProps) => {
  const [schoolAdmin, setSchoolAdmin] =
    useState<null | Tables<"profiles_with_user">>(null);
  const { getSchoolAdmin } = useSchools();
  useEffect(() => {
    if (school) {
      getSchoolAdmin(school.id)
        .then((admin) => {
          console.log("Fetched admin:", admin);
          setSchoolAdmin(admin);
        })
        .catch(() => {
          // setSchoolAdmin(null);
        });
    }
  }, [school]);

  // if (!school) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "نشط";
      case "inactive":
        return "متوقف";
      case "expired":
        return "منتهي الصلاحية";
      default:
        return "غير محدد";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("ar-SA");
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 [&>button]:right-auto [&>button]:left-4 [&>button]:text-white [&>button]:hover:text-white/80"
        dir="rtl"
      >
        <div className="space-y-6">
          {/* Header with background */}
          <div className="bg-gradient-to-r from-school-navy/90 to-school-blue/90 text-white p-6 rounded-t-lg">
            <div className="flex items-center gap-4">
              <Database className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">
                  بيانات {school?.school_name}
                </h1>
              </div>
            </div>
          </div>

          <div className="px-6">
            {/* معلومات المدرسة - الصف الأول */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Card className="bg-gradient-to-br from-blue-50/80 to-indigo-100/80 backdrop-blur-sm border-blue-200/50 border-l-4 border-l-blue-600">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base text-gray-600">اسم المدرسة</span>
                    <span className="text-base font-bold text-blue-600">
                      {school?.school_name}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50/80 to-violet-100/80 backdrop-blur-sm border-purple-200/50 border-l-4 border-l-purple-600">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base text-gray-600">عدد الطلاب</span>
                    <span className="text-base font-bold text-purple-600">
                      {toArabicNumbers(school?.students_count || 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* معلومات المدرسة - الصف الثاني */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-green-50/80 to-emerald-100/80 backdrop-blur-sm border-green-200/50 border-l-4 border-l-green-600">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base text-gray-600">رقم الهاتف</span>
                    <span className="text-base font-bold text-green-600">
                      {school?.phone || "غير محدد"}{" "}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50/80 to-amber-100/80 backdrop-blur-sm border-orange-200/50 border-l-4 border-l-orange-600">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base text-gray-600">
                      حالة المدرسة
                    </span>
                    <Badge className={getStatusColor(school?.status)}>
                      {getStatusLabel(school?.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* معلومات تفصيلية */}
            <Card className="bg-gradient-to-br from-gray-50/80 to-slate-100/80 backdrop-blur-sm border-gray-200/50">
              <CardHeader>
                <CardTitle>معلومات مدير المدرسة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      البريد الإلكتروني
                    </Label>
                    <p className="text-lg">{schoolAdmin?.email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      الأسم الكامل{" "}
                    </Label>
                    <p className="text-lg">
                      {schoolAdmin?.raw_user_meta_data?.["full_name"] || "غير محدد"}
                    </p>
                  </div>{" "}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      رقم الهاتف
                    </Label>
                    <p className="text-lg">{schoolAdmin?.phone || "غير محدد"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      تاريخ التسجيل
                    </Label>
                    <p className="text-lg">{formatDate(schoolAdmin?.user_created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* زر الإغلاق في الأسفل يمين */}
            <div className="flex justify-end mt-6 mb-6">
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-school-navy hover:bg-school-navy/90 text-white px-8 py-2"
              >
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SchoolDataView;
